import React, { useEffect, useState } from 'react';
import { FiFileText, FiUsers, FiPackage, FiDollarSign, FiPlus, FiAlertTriangle, FiX, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import AppTour from '../../../../components/common/AppTour';
import './Dashboard.css';

const Dashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [gmailLinked, setGmailLinked] = useState(true);
    const [gmailBannerDismissed, setGmailBannerDismissed] = useState(
        () => sessionStorage.getItem('gmailBannerDismissed_dashboard') === 'true'
    );
    const [runTour, setRunTour] = useState(false);
    const [finalStepOnly, setFinalStepOnly] = useState(false);
    const [stats, setStats] = useState({
        cotizaciones: 0,
        clientes: 0,
        productos: 0,
        ingresos: 0,
    });
    const [recentQuotations, setRecentQuotations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('tour') === 'dashboard') {
            setRunTour(true);
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        fetchDashboardData();
        checkGmailStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDashboardData = async () => {
        try {
            // Obtener estadísticas
            const [cotizacionesRes, clientesRes, productosRes] = await Promise.all([
                api.get('/cotizaciones/'),
                api.get('/clientes/'),
                api.get('/productos/'),
            ]);

            setStats({
                cotizaciones: cotizacionesRes.data.count || cotizacionesRes.data.results?.length || 0,
                clientes: clientesRes.data.count || clientesRes.data.results?.length || 0,
                productos: productosRes.data.count || productosRes.data.results?.length || 0,
                ingresos: calculateIngresos(cotizacionesRes.data.results || []),
            });

            // Obtener cotizaciones recientes (últimas 5)
            const recent = (cotizacionesRes.data.results || []).slice(0, 5);
            setRecentQuotations(recent);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkGmailStatus = async () => {
        try {
            const res = await api.get('/empresa/mi_empresa/');
            setGmailLinked(!!res.data.usa_gmail_propio);
        } catch (err) {
            console.error('Error checking Gmail status:', err);
        }
    };

    const calculateIngresos = (cotizaciones) => {
        return cotizaciones
            .filter((c) => c.estado === 'ACEPTADA')
            .reduce((sum, c) => sum + parseFloat(c.total || 0), 0);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
        }).format(value);
    };

    const formatCompactNumber = (value) => {
        if (value >= 10000000) {
            return (value / 1000000).toLocaleString('es-CL', { 
                maximumFractionDigits: 1,
                minimumFractionDigits: 1 
            }) + ' mill';
        }
        return new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(value);
    };

    const getStatusBadge = (status) => {
        const badges = {
            BORRADOR: 'badge-secondary',
            ENVIADA: 'badge-info',
            ACEPTADA: 'badge-success',
            RECHAZADA: 'badge-danger',
        };
        return badges[status] || 'badge-secondary';
    };

    const getStatusLabel = (status) => {
        const labels = {
            BORRADOR: 'Borrador',
            ENVIADA: 'Enviada',
            ACEPTADA: 'Aceptada',
            RECHAZADA: 'Rechazada',
        };
        return labels[status] || status;
    };

    let currentTourSteps = [];

    if (finalStepOnly) {
        currentTourSteps = [{
            element: '.dashboard-header',
            intro: '¡Tour del módulo completado! Ya conoces todas las pantallas del sistema de ventas. ¡Mucho éxito en tu gestión!',
            position: 'bottom',
        }];
    } else {
        currentTourSteps = [
            {
                element: '.dashboard-header',
                intro: '<strong>Tu Resumen de Ventas</strong>Bienvenido a CotizaPlus. Aquí tendrás un vistazo rápido y en tiempo real de cómo le va a tu negocio.',
                position: 'bottom',
            },
            {
                element: '.dashboard-stats',
                intro: user?.rol === 'ADMIN' 
                    ? '<strong>Tus Números en Tiempo Real</strong>Estas tarjetas muestran cuántos clientes tienes, tus productos y el dinero que ha ingresado por ventas este mes.'
                    : '<strong>Tus Números en Tiempo Real</strong>Estas tarjetas muestran cuántos clientes tienes y tus productos activos en el catálogo.',
                position: 'top',
            },
            {
                element: '.dashboard-table-container',
                intro: '<strong>Gráfica de Ventas</strong>Visualiza cómo han ido tus ventas aprobadas a lo largo del tiempo. Ideal para saber si vas por buen camino.',
                position: 'top',
            },
            {
                element: '#tour-sidebar-productos',
                intro: '<strong>Catálogo de Productos</strong>Administra aquí tus bienes y servicios. Tip: sube imágenes atractivas para que se vean increíbles en los PDFs.',
                position: 'right',
            },
            {
                element: '#tour-sidebar-clientes',
                intro: '<strong>Directorio de Clientes</strong>Crea fichas para tus prospectos y empresas, con toda su información en un solo lugar.',
                position: 'right',
            },
            {
                element: '#tour-sidebar-cotizaciones',
                intro: '<strong>Centro de Cotizaciones</strong>Aquí crearás, enviarás y aprobarás todas las propuestas comerciales a tus clientes.',
                position: 'right',
            }
        ];
    }

    if (loading) {
        return (
            <div className="loading-overlay">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={currentTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: finalStepOnly ? '¡Entendido!' : 'Continuar a Productos' }}
                    onTourComplete={() => {
                        if (!finalStepOnly) {
                            window.location.href = '/app/cotizaplus/productos?tour=productos';
                        } else {
                            setFinalStepOnly(false); // Reset
                        }
                    }}
                    onTourExit={() => setFinalStepOnly(false)}
                />
            )}

            {/* Gmail Warning Banner */}
            {user?.rol === 'ADMIN' && !gmailLinked && !gmailBannerDismissed && (
                <div className="gmail-banner">
                    <div className="gmail-banner-content">
                        <div className="gmail-banner-icon">
                            <FiAlertTriangle size={22} />
                        </div>
                        <div>
                            <strong className="gmail-banner-title">
                                Vincula tu cuenta de Google
                            </strong>
                            <p className="gmail-banner-text gmail-banner-text-full">
                                Para enviar cotizaciones por correo y gestionar empleados, es necesario vincular una cuenta de Google.
                            </p>
                            <p className="gmail-banner-text gmail-banner-text-short">
                                Necesitas vincular Google para usar todas las funciones.
                            </p>
                        </div>
                    </div>
                    <div className="gmail-banner-actions">
                        <button
                            className="gmail-banner-btn"
                            onClick={() => navigate('/app/configuracion')}
                        >
                            Configurar →
                        </button>
                        <button
                            className="gmail-banner-close"
                            onClick={() => {
                                sessionStorage.setItem('gmailBannerDismissed_dashboard', 'true');
                                setGmailBannerDismissed(true);
                            }}
                            title="Cerrar alerta"
                        >
                            <FiX size={18} />
                        </button>
                    </div>
                </div>
            )}

            <div className="dashboard-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h1 style={{ marginBottom: 0 }}>Dashboard</h1>
                        <SectionHelpModal
                            title="¿Qué ves en el Dashboard?"
                            content={
                                <div>
                                    <p>Este es el panel principal de CotizaPlus donde tendrás un resumen instantáneo de tus ventas:</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Tarjetas de Estadísticas:</strong> Las cajas de colores muestran totales rápidos como cuántos clientes tienes{user?.rol === 'ADMIN' ? ' y tus ingresos del mes calculados según las cotizaciones "Aceptadas"' : ' y tus productos registrados'}.</li>
                                        <li><strong>Cotizaciones Recientes:</strong> En la tabla de abajo verás los últimos 5 documentos que tú o tu equipo han generado, para acceder a ellos rápidamente.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Bienvenido a tu panel de control</p>
                </div>
                <button
                    className="btn-custom btn-primary btn-lg"
                    onClick={() => navigate('/app/cotizaplus/cotizaciones/nuevo')}
                >
                    <FiPlus size={20} />
                    Nueva Cotización
                </button>
            </div>

            {/* Tarjetas de estadísticas */}
            <div className="dashboard-stats">
                <div className="card-stat">
                    <div className="card-stat-icon">
                        <FiFileText size={32} />
                    </div>
                    <div>
                        <div className="card-stat-number">{stats.cotizaciones.toLocaleString('es-CL')}</div>
                        <div className="card-stat-label">Total Cotizaciones</div>
                    </div>
                </div>

                <div className="card-stat">
                    <div className="card-stat-icon">
                        <FiUsers size={32} />
                    </div>
                    <div>
                        <div className="card-stat-number">{stats.clientes.toLocaleString('es-CL')}</div>
                        <div className="card-stat-label">Clientes Activos</div>
                    </div>
                </div>

                <div className="card-stat">
                    <div className="card-stat-icon">
                        <FiPackage size={32} />
                    </div>
                    <div>
                        <div className="card-stat-number">{stats.productos.toLocaleString('es-CL')}</div>
                        <div className="card-stat-label">Productos</div>
                    </div>
                </div>

                {user?.rol === 'ADMIN' && (
                    <div className="card-stat">
                        <div className="card-stat-icon">
                            <FiDollarSign size={32} />
                        </div>
                        <div>
                            <div className="card-stat-number" style={{ fontSize: stats.ingresos >= 10000000 ? '2.2rem' : '1.8rem' }}>
                                {formatCompactNumber(stats.ingresos)}
                            </div>
                            <div className="card-stat-label">Ingresos del Mes</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Cotizaciones recientes */}
            <div className="card-custom" id="tour-dashboard-recent">
                <div className="card-header-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0 }}>Cotizaciones Recientes</h3>
                    <button
                        className="btn-custom"
                        style={{ fontSize: '13px', padding: '6px 12px', background: '#f8f9fa', color: '#333', border: '1px solid #ddd' }}
                        onClick={() => navigate('/app/cotizaplus/cotizaciones')}
                    >
                        Ver todas
                    </button>
                </div>

                {recentQuotations.length === 0 ? (
                    <p className="text-muted">No hay cotizaciones recientes</p>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Número</th>
                                        <th>Cliente</th>
                                        <th>Fecha</th>
                                        <th>Total</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentQuotations.map((cotizacion) => (
                                        <tr key={cotizacion.id}>
                                            <td>{cotizacion.numero}</td>
                                            <td>{cotizacion.cliente_info?.nombre || 'N/A'}</td>
                                            <td>{new Date(cotizacion.fecha_creacion).toLocaleDateString('es-CL')}</td>
                                            <td>{formatCurrency(cotizacion.total)}</td>
                                            <td>
                                                <span className={`badge-custom ${getStatusBadge(cotizacion.estado)}`}>
                                                    {getStatusLabel(cotizacion.estado)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-block d-lg-none mt-3">
                            {recentQuotations.map((cotizacion) => (
                                <div key={cotizacion.id} className="mobile-card">
                                    <div className="mobile-card-header">
                                        <span className="mobile-card-id" style={{ fontWeight: 'bold' }}>#{cotizacion.numero}</span>
                                        <span className={`badge-custom ${getStatusBadge(cotizacion.estado)}`}>
                                            {getStatusLabel(cotizacion.estado)}
                                        </span>
                                    </div>
                                    <div className="mobile-card-body">
                                        <div style={{ fontWeight: '500', marginBottom: '8px' }}>
                                            {cotizacion.cliente_info?.nombre || 'N/A'}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span style={{ color: 'var(--gray-500)', fontSize: '14px' }}>Fecha:</span>
                                            <span style={{ fontSize: '14px' }}>{new Date(cotizacion.fecha_creacion).toLocaleDateString('es-CL')}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', marginTop: '8px' }}>
                                            <span style={{ color: 'var(--text-dark)', fontWeight: '500' }}>Total:</span>
                                            <span style={{ color: 'var(--primary-orange)', fontWeight: 'bold' }}>{formatCurrency(cotizacion.total)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};

export default Dashboard;
